import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
} from "react";

// PayoutStatus based on PayoutStatusSchema
export type PayoutStatus = "REQUESTED" | "IN_PROGRESS" | "COMPLETED" | "FAILED";

export interface PayoutHistoryItem {
  id: string;
  user_id: string;
  status: PayoutStatus;
  amount: number;
  currency: string;
  requested_at: string;
  processed_at?: string | null;
  completed_at?: string | null;
}

export interface PayoutState {
  total_approved: number;
  total_paid: number;
  total_in_review: number;
  total_rejected: number;
  payout_error_message: string | null;
  history: PayoutHistoryItem[];
}

interface PayoutContextType {
  payoutData: PayoutState;
  isLoading: boolean;
  error: string | null;
  fetchPayoutData: (token: string) => Promise<void>;
}

// Create the context
const PayoutContext = createContext<PayoutContextType | undefined>(undefined);

// Initial state for payoutData
const initialPayoutState: PayoutState = {
  total_approved: 0,
  total_paid: 0,
  total_in_review: 0,
  total_rejected: 0,
  payout_error_message: null,
  history: [],
};

interface PayoutProviderProps {
  children: ReactNode;
}

export const PayoutProvider: React.FC<PayoutProviderProps> = ({ children }) => {
  const [payoutData, setPayoutData] = useState<PayoutState>(initialPayoutState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPayoutData = useCallback(
    async (userToken: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const earningsSummaryUrl = `${process.env.REACT_APP_API_URL}/payout/earnings-summary`;
        const historyUrl = `${process.env.REACT_APP_API_URL}/payout/history`;

        const headers = {
          Authorization: `bearer ${userToken}`,
          Accept: "application/json",
        };

        const [earningsResponse, historyResponse] = await Promise.all([
          fetch(earningsSummaryUrl, { headers }),
          fetch(historyUrl, { headers }),
        ]);

        if (!earningsResponse.ok) {
          throw new Error(
            `Failed to fetch earnings summary: ${earningsResponse.statusText}`,
          );
        }
        if (!historyResponse.ok) {
          throw new Error(
            `Failed to fetch payout history: ${historyResponse.statusText}`,
          );
        }

        const earningsData = await earningsResponse.json();
        const historyData: PayoutHistoryItem[] = await historyResponse.json();

        setPayoutData({
          total_approved: earningsData.total_approved || 0,
          total_paid: earningsData.total_paid || 0,
          total_in_review: earningsData.total_in_review || 0,
          total_rejected: earningsData.total_rejected || 0,
          payout_error_message: earningsData.payout_error_message || null,
          history: historyData || [],
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An unknown error occurred";
        setError(errorMessage);
        // Optionally reset state on error or keep stale data
        // setPayoutData(initialPayoutState);
      } finally {
        setIsLoading(false);
      }
    },
    [setPayoutData, setIsLoading, setError], // Dependencies for useCallback
  );

  const value = {
    payoutData,
    isLoading,
    error,
    fetchPayoutData,
  };

  return (
    <PayoutContext.Provider value={value}>{children}</PayoutContext.Provider>
  );
};

// Custom hook to use the PayoutContext
export const usePayouts = (): PayoutContextType => {
  const context = useContext(PayoutContext);
  if (context === undefined) {
    throw new Error("usePayouts must be used within a PayoutProvider");
  }
  return context;
};
